import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_STATE } from '../lib/stateCore.mjs';
import type { StateSlug } from '../lib/stateCore.mjs';
import { findLaunchedStateIndex } from '../lib/stateRoutes';

/**
 * The launched state the CURRENT route is scoped to, or DEFAULT_STATE when the
 * route is state-less (`/`, `/gyms`, `/favourites`, blog, legacy paths).
 *
 * R-02: every state-capable callable (searchGymsFromDisplay, autocompleteLocation,
 * resolveLocation, …) defaults to NSW when the client omits `state`, so a VIC page
 * that fails to send it silently searches the NSW corpus. This hook is the single
 * client-side derivation of "which state is this page" — the same classifier the
 * route layer uses (findLaunchedStateIndex), so it cannot disagree with routing.
 *
 * State-less routes deliberately resolve to DEFAULT_STATE, matching the servers'
 * own absent-input behaviour (P2-A rule: absent → default state). Re-authoring
 * those surfaces per-state is SITE 8, an editorial ruling — not this hook's job.
 */
/**
 * The launched state the current route is scoped to, or **null** when the route
 * carries none — the same classifier as `useRouteStateSlug`, with the DEFAULT_STATE
 * arm withheld.
 *
 * ⚠️ WHY A SECOND ACCESSOR RATHER THAN A SECOND DERIVATION (UI-24's property, kept).
 * `useRouteStateSlug` collapses "this route is NSW" and "this route has no state" into
 * the same answer. That is CORRECT for anything that must query a corpus — the servers
 * default to NSW when `state` is absent, so matching them is the safe behaviour — and
 * WRONG for anything that makes a CLAIM ABOUT COVERAGE, because a state-less surface
 * answering "nsw" turns "gyms across NSW and VIC" into "gyms across NSW" on a page that
 * really does span both.
 *
 * ⭐ So this is the honest base and `useRouteStateSlug` is a thin fallback wrapper over
 * it. One classifier, two arms, and the fallback is now visible at the call site instead
 * of hidden inside the only available accessor. **Do not re-derive the state inline from
 * `findLaunchedStateIndex` at a call site — that is exactly the duplication UI-24 removed**
 * (it once built a first-state URL on a second-state page and navigated the visitor there
 * with `replace: true`, destroying the way back).
 *
 * COVERS the "which state, honestly" question. DOES NOT decide what to render for null —
 * that is the caller's editorial choice.
 */
export function useRouteStateSlugOrNull(): StateSlug | null {
    const { pathname } = useLocation();
    return useMemo(() => {
        const parts = pathname.split('/');
        const idx = findLaunchedStateIndex(parts);
        return idx >= 0 ? (parts[idx] as StateSlug) : null;
    }, [pathname]);
}

export function useRouteStateSlug(): StateSlug {
    return useRouteStateSlugOrNull() ?? DEFAULT_STATE;
}
